import type { ReactElement } from "react";
import { ResponsiveContainer } from "recharts";

import { Skeleton } from "@/components/ui/skeleton";

interface ChartFrameProps {
  /** Announced to screen readers — a chart with no stated subject is unreadable. */
  title: string;
  /** A single Recharts chart element. The frame owns sizing; the module owns the series. */
  children: ReactElement;
  height?: number;
  pending?: boolean;
  /** How many points the module is about to render — the escalation signal below. */
  pointCount?: number;
  emptyMessage?: string;
  /** Render `title` (and `subtitle`) as a visible heading above the chart. */
  titleVisible?: boolean;
  /** Shown under the title, only when `titleVisible`. */
  subtitle?: string;
}

/**
 * The ONE charting seam (C3, I-7).
 *
 * A module never picks a charting library: Recharts is the default, Tremor is
 * for KPI cards, ECharts is for >10k points, and that choice is made here once
 * rather than three ways across the estate — three chart idioms is precisely
 * the fragmentation this portal exists to end (dev spec §3 charts row).
 *
 * Above the ECharts threshold the frame does not silently render a Recharts
 * chart that will crawl; it says so, because a module quietly shipping 40k
 * points into the default library is a performance failure nobody attributes
 * correctly later.
 */
export const ECHARTS_THRESHOLD = 10_000;

export function ChartFrame({
  title,
  children,
  height = 280,
  pending = false,
  pointCount,
  emptyMessage = "Nothing to chart for this selection.",
  titleVisible = false,
  subtitle,
}: ChartFrameProps) {
  const heading = titleVisible ? (
    <figcaption className="mb-3">
      <h2 className="text-base font-semibold text-ks-ink">{title}</h2>
      {subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
    </figcaption>
  ) : null;

  if (pending) {
    return (
      <figure aria-label={title}>
        {heading}
        <Skeleton style={{ height }} className="w-full" />
      </figure>
    );
  }

  if (pointCount === 0) {
    return (
      <figure aria-label={title} className="text-ks-ink">
        {heading}
        <p style={{ height }} className="flex items-center text-sm">
          {emptyMessage}
        </p>
      </figure>
    );
  }

  return (
    <figure aria-label={title}>
      {heading}
      {pointCount != null && pointCount > ECHARTS_THRESHOLD && (
        <p role="status" className="pb-2 text-sm text-ks-danger">
          {pointCount.toLocaleString()} points is above the {ECHARTS_THRESHOLD.toLocaleString()}
          -point limit for this chart — aggregate server-side or move this view to ECharts.
        </p>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
    </figure>
  );
}
